// Main-thread facade for searchWorker.js (request ids, timeouts, progress).
import { isEncrypted, getKeys, onKeys } from "./secure.js";
let worker = null;
let seq = 0;
const pending = new Map();
const progressListeners = new Set();
let initPromise = null;
let initTenant = null;

function ensureWorker() {
  if (worker) return worker;
  worker = new Worker(new URL("./searchWorker.js", import.meta.url), { type: "module" });
  worker.onmessage = (e) => {
    const m = e.data || {};
    if (m.type === "progress") { progressListeners.forEach((fn) => { try { fn(m); } catch { /* ignore */ } }); return; }
    const p = pending.get(m.id);
    if (!p) return;
    pending.delete(m.id);
    clearTimeout(p.timer);
    if (m.ok) p.resolve(m.result); else p.reject(new Error(m.error || "search worker error"));
  };
  worker.onerror = (e) => {
    const err = new Error(e?.message || "search worker crashed");
    for (const p of pending.values()) { clearTimeout(p.timer); p.reject(err); }
    pending.clear();
    initPromise = null;
    try { worker.terminate(); } catch { /* ignore */ }
    worker = null;
  };
  return worker;
}

function call(type, payload = {}, timeoutMs = 20000) {
  const w = ensureWorker();
  const id = ++seq;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => { pending.delete(id); reject(new Error(`search worker timeout (${type})`)); }, timeoutMs);
    pending.set(id, { resolve, reject, timer });
    w.postMessage({ id, type, ...payload });
  });
}

onKeys(() => { initPromise = null; });

export const searchClient = {
  init(tenant) {
    if (!initPromise || initTenant !== tenant) {
      initTenant = tenant;
      initPromise = call("init", { tenant, encrypted: isEncrypted(), keys: getKeys() }, 60000).catch((err) => { initPromise = null; throw err; });
    }
    return initPromise;
  },
  async build(tenant) {
    await this.init(tenant);
    const result = await call("build", {}, 10 * 60 * 1000);
    initPromise = Promise.resolve({ state: "fresh", tenant, ...result });
    return result;
  },
  search: (term) => call("search", { term }, 20000),
  status: () => call("status", {}, 5000),
  onProgress(fn) { progressListeners.add(fn); return () => progressListeners.delete(fn); },
  isSupported: () => typeof Worker !== "undefined",
};
